import { TodoInputBlock } from '@coast/schemas';
import { useCallback, useContext, useState } from 'react';
import { Keyboard, TextInput } from 'react-native';
import { MBClearButton } from '@shared/components/MBButton';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBAnimationBounceIn } from '@shared/components/animation';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { CardRenderContext } from '@shared/components/blocks/contexts/render';
import { CardThemeContext } from '@shared/components/blocks/contexts/theme';
import { TodoItem } from '@shared/components/blocks/types';
import { MBIconV2 } from '@shared/components/image';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTypographyStyles } from '@shared/components/typography';
import { ColorThemeContext, Colors } from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { ChannelPermissionsContext } from '@shared/util/permissions/ChannelPermissionsContext';
import { usePermission } from '@shared/util/permissions/hooks';
import { useScrollTextInputIntoView } from './hooks';

interface Props {
  todo: TodoItem;
  block: TodoInputBlock;
  onRemove(id: string): void;
  onUpdate(item: TodoItem): Promise<void> | void;
}

export function MBTodoItemRow({ todo, block, onRemove, onUpdate }: Props) {
  const colors = useContext(ColorThemeContext);
  const renderContext = useContext(CardRenderContext);
  const cardTheme = useContext(CardThemeContext);
  const completed = todo.status === 'COMPLETED';
  const [loading, setLoading] = useState(false);
  const { channelId } = useContext(ChannelPermissionsContext);
  const updatePermission = usePermission({ action: 'update', subject: 'Card', channelId });
  const { isSubmitting } = useCardFormContext();
  const isDesktop = useIsDesktop();

  const handleClear = useCallback(() => onRemove(todo.id), [onRemove, todo.id]);
  const handleChangeText = useCallback(
    (summary: string) => onUpdate({ ...todo, summary }),
    [onUpdate, todo],
  );
  const handleToggleStatus = useCallback(async () => {
    const maybePromise = onUpdate({
      ...todo,
      status: todo.status === 'COMPLETED' ? 'NEEDS-ACTION' : 'COMPLETED',
    });
    if (maybePromise instanceof Promise) {
      try {
        setLoading(true);
        await maybePromise;
      } finally {
        setLoading(false);
      }
    }
  }, [onUpdate, todo]);

  const scrollTextInputIntoView = useScrollTextInputIntoView();

  return (
    <View row paddingLeft={2} paddingRight={3}>
      <MBTouchableOpacity
        disabled={isSubmitting || !updatePermission.enabled}
        onPress={handleToggleStatus}
      >
        <MBView paddingHorizontal={1} style={isSubmitting && { opacity: 0.7 }}>
          {completed ? (
            <MBAnimationBounceIn>
              <MBIconV2
                name="task-filled"
                color={cardTheme === 'stale' ? colors.foregroundInactive : colors.accent}
              />
            </MBAnimationBounceIn>
          ) : (
            <MBIconV2
              name="unfilled-circle"
              color={cardTheme === 'stale' ? colors.foregroundInactive : colors.accentInactive}
            />
          )}
          {loading && (
            <MBLoadingIndicator
              absolute
              center
              color={colors.accent}
              style={{ backgroundColor: Colors.overlay.white70 }}
            />
          )}
        </MBView>
      </MBTouchableOpacity>
      <View paddingRight={renderContext === 'form' ? 1 : 0} />

      {block.mode === 'completion' ? (
        <View flex centerVertically paddingVertical={0.5}>
          <MBTextBody1
            lineThrough={completed}
            color={completed ? colors.foregroundInactive : colors.foregroundActive}
          >
            {todo.summary}
          </MBTextBody1>
        </View>
      ) : (
        <>
          {isDesktop && (
            <MBAutosizeTextInput
              value={todo.summary}
              returnKeyType="done"
              padding="none"
              blurOnSubmit
              placeholder={i18n('card.blocks.todoInput.addPlaceholder')}
              color={completed ? colors.foregroundInactive : colors.foregroundActive}
              onFocus={scrollTextInputIntoView}
              onChangeText={handleChangeText}
              onSubmitEditing={Keyboard?.dismiss}
              style={{
                flex: 1,
                alignSelf: 'center',
                marginTop: 4,
                marginBottom: 4,
                textDecorationLine: completed ? 'line-through' : 'none',
              }}
            />
          )}
          {!isDesktop && (
            <View flex style={{ alignSelf: 'center' }}>
              <TextInput
                value={todo.summary}
                returnKeyType="done"
                blurOnSubmit
                textAlignVertical={'center'}
                placeholder={i18n('card.blocks.todoInput.addPlaceholder')}
                onFocus={scrollTextInputIntoView}
                onChangeText={handleChangeText}
                onSubmitEditing={Keyboard?.dismiss}
                autoFocus={block.autoFocus}
                multiline
                style={[
                  MBTypographyStyles.body1,
                  {
                    textDecorationLine: completed ? 'line-through' : 'none',
                    color: completed ? colors.foregroundInactive : colors.foregroundActive,
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                ]}
              />
            </View>
          )}
        </>
      )}

      {block.mode !== 'completion' && <MBClearButton onPress={handleClear} />}
    </View>
  );
}
